import {
    CALENDAR_BASED_QUESTION_SELECTORS as CALENDAR_SELECTOR,
    DateTimeFormats,
    QUESTION_TYPES as Q_TYPES,
    SESSION_STATUSES,
    TRIGGERING_LOGIC_TYPES,
    UNANSWERED_QUESTION_REASONS as UNANSWERED_REASONS,
} from '$app-web/constants';
import { uploadMediaOnSurveySession } from '$app-web/endpoints';
import type { Answer, ObjectType, Resp, Session, SessionResponse } from '$app-web/types';
import { appDateTime, isSuperset, omitObjectKeys, pickObjectKeys } from '$app-web/utils';

import { PureStore, StoreQuestion } from '../store/types';
import { isAnswerValueEmpty } from './helpers';

export async function processQuestionsAnswersForBackend(params: {
    sessionUuid: string;
    userId: number;
    questions: Array<StoreQuestion>;
}) {
    const { sessionUuid, userId, questions } = params;

    const updatedQuestions: Array<StoreQuestion> = [];

    try {
        for await (const question of questions) {
            const answer = question.answer;
            // Image - Audio - Video
            if (answer instanceof File) {
                const fileUrl = await uploadMediaOnSurveySession({ sessionUuid, userId, file: answer });

                if (fileUrl != null) {
                    updatedQuestions.push({ ...question, answer: fileUrl });
                } else {
                    throw Error();
                }
            }
            // Text/Audio
            else if ((answer as ObjectType)?.audioUrl instanceof File) {
                const { text, audioUrl: file } = answer as { audioUrl: File; text: string };
                const data = { sessionUuid, userId, file };
                const fileUrl = await uploadMediaOnSurveySession(data);

                if (fileUrl != null) {
                    updatedQuestions.push({ ...question, answer: { text, audioUrl: fileUrl } });
                } else {
                    throw Error();
                }
            } else {
                updatedQuestions.push(question);
            }
        }

        return updatedQuestions;
    } catch {
        return [];
    }
}

function getStringifiedDate(date: unknown, selector?: string) {
    if (date == null) {
        return undefined;
    }

    if (selector === CALENDAR_SELECTOR.TIME) {
        return appDateTime(date as string).format(DateTimeFormats.Time);
    }
    if (selector === CALENDAR_SELECTOR.DATE) {
        return appDateTime(date as string).format(DateTimeFormats.Date);
    }

    return appDateTime(date as string).format(DateTimeFormats.DateTime);
}

function getRespFieldOfQuestion({
    question,
    expiryTimeMs,
    allCurrentSurveyQuestions,
    tokens,
    isPublicSurvey = false,
}: {
    question: StoreQuestion;
    expiryTimeMs: number;
    allCurrentSurveyQuestions: Array<StoreQuestion>;
    tokens: PureStore['tokens'];
    isPublicSurvey?: boolean;
}): Array<Partial<Resp>> {
    const {
        answer,
        enabled,
        answers = [],
        selector,
        questionType,
        isEnabledByAnswers,
        isHiddenByFlow,
        isHiddenByCriteria,
        isHiddenBySectionsFlow,
        isHiddenByNoSectionLoop,
        isHiddenByRandomization,
        isHiddenBySectionCriteria,
        isHiddenBySectionsRandomization,
        carryForwardQuestion,
        orderId,
        isSkipped,
    } = question;

    if (expiryTimeMs < Date.now() && isAnswerValueEmpty(answer)) {
        return [{ unansweredReason: UNANSWERED_REASONS.SESSION_STATUS }];
    } else if (isHiddenBySectionsRandomization) {
        return [{ unansweredReason: UNANSWERED_REASONS.SECTION_RANDOMIZATION }];
    } else if (isHiddenByRandomization) {
        return [{ unansweredReason: UNANSWERED_REASONS.QUESTION_RANDOMIZATION }];
    } else if (isHiddenBySectionCriteria) {
        return [{ unansweredReason: UNANSWERED_REASONS.SECTION_CRITERIA }];
    } else if (isHiddenByCriteria) {
        return [{ unansweredReason: UNANSWERED_REASONS.QUESTION_CRITERIA }];
    } else if (isHiddenByNoSectionLoop) {
        return [{ unansweredReason: UNANSWERED_REASONS.NO_SECTION_LOOP }];
    } else if (!enabled && !isEnabledByAnswers) {
        return [{ unansweredReason: UNANSWERED_REASONS.DISABLED_QUESTION }];
    } else if (isHiddenByFlow || (isHiddenBySectionsFlow && isHiddenByFlow !== false)) {
        return [{ unansweredReason: UNANSWERED_REASONS.SECTIONS_FLOW }];
    } else if (carryForwardQuestion?.questionId && isAnswerValueEmpty(answer)) {
        const senderQuestionIdString = carryForwardQuestion.questionId;
        const isExternalCarryForward = senderQuestionIdString.includes('_');
        const senderQuestionId = parseInt(senderQuestionIdString.replace('Q', ''), 10);
        /*
         We have three cases for questions that have carry forward answers and are unanswered.
         1. It's in public survey and the sender question is in another survey or comes after the receiver question.
         2. The question was supposed to show 'unselected' (a.k.a unchecked) options, but all answers were selected in sender question.
         3. The sender question was skipped (therefore no answer was provided for receiver question).
         4. The receiver question skipped.
        */

        // Case 1
        if ((isExternalCarryForward || (orderId != null && senderQuestionId > orderId)) && isPublicSurvey) {
            return [{ unansweredReason: UNANSWERED_REASONS.SENDER_RESPONSE_NOT_AVAILABLE }];
        }

        // Case 4
        if (isSkipped) {
            return [{ unansweredReason: UNANSWERED_REASONS.SKIPPED }];
        }

        // Case 2, when sender question is in another survey
        if (isExternalCarryForward && carryForwardQuestion?.type === 'unchecked') {
            const senderQuestion = tokens?.[senderQuestionIdString];
            if (senderQuestion) {
                const senderQuestionAnswer = senderQuestion.value;
                if (Array.isArray(senderQuestionAnswer) && senderQuestionAnswer.length > 0) {
                    return [{ unansweredReason: UNANSWERED_REASONS.SENDER_RESPONSE_NO_OPTIONS }];
                }
            }
        }
        // Case 2, when sender question is in another survey
        if (!isExternalCarryForward && carryForwardQuestion?.type === 'unchecked') {
            const senderQuestion = allCurrentSurveyQuestions.find((questionItem) => questionItem.questionId === senderQuestionId);
            if (senderQuestion) {
                if (Array.isArray(senderQuestion.answer) && senderQuestion.answer.length > 0) {
                    return [{ unansweredReason: UNANSWERED_REASONS.SENDER_RESPONSE_NO_OPTIONS }];
                }
            }
        }

        // Case 4
        return [{ unansweredReason: UNANSWERED_REASONS.SENDER_QUESTION_NOT_ANSWERED }];
    } else if (isAnswerValueEmpty(answer)) {
        return [{ unansweredReason: UNANSWERED_REASONS.SKIPPED }];
    } else {
        switch (questionType) {
            case Q_TYPES.INFORMATION:
                return [{}];
            case Q_TYPES.FREE_FORM_TEXT:
            case Q_TYPES.BARCODE:
            case Q_TYPES.NUMBER:
            case Q_TYPES.VAS:
            case Q_TYPES.LENGTH:
            case Q_TYPES.MASS:
                return [{ answerContent: answer as string | number }];
            case Q_TYPES.IMAGE:
            case Q_TYPES.AUDIO:
            case Q_TYPES.VIDEO:
                return [{ answerUrl: answer as string }];
            case Q_TYPES.CALENDAR: {
                const [startDate, endDate] = answer as [unknown, unknown];

                return [
                    {
                        answerContent: getStringifiedDate(startDate, selector),
                        periodEnd: getStringifiedDate(endDate, selector),
                        selector,
                    },
                ];
            }
            case Q_TYPES.AUDIO_TEXT: {
                const { text, audioUrl } = answer as { text: string; audioUrl: string | null };

                if (audioUrl == null) return [{ answerContent: text }];
                else return [{ answerContent: text }, { answerUrl: audioUrl }];
            }
            case Q_TYPES.SINGLE_CHOICE: {
                const relatedAnswer = (answers.find(({ answerId }) => `${answerId}` === answer) ?? {}) as Answer;
                const requiredAnswerProps = pickObjectKeys(relatedAnswer, ['answerId', 'answerContent', 'answerUrl']);
                return [{ ...requiredAnswerProps }];
            }
            case Q_TYPES.MULTIPLE_CHOICE:
                return (answer as Array<number>).map((item) => {
                    const relatedAnswer = (answers.find(({ answerId }) => `${answerId}` === `${item}`) ?? {}) as Answer;
                    const requiredAnswerProps = pickObjectKeys(relatedAnswer, ['answerId', 'answerContent', 'answerUrl']);
                    return { ...requiredAnswerProps };
                });
            default:
                return [{}];
        }
    }
}

const getFullAnswerOptionsOfChoiceBasedQuestions = (question: StoreQuestion) => {
    if (question.questionType === Q_TYPES.SINGLE_CHOICE || question.questionType === Q_TYPES.MULTIPLE_CHOICE) {
        const answerOptions = question.answers ?? [];
        return answerOptions.map((item) => {
            const validAnswerOptions = pickObjectKeys(item, ['answerId', 'answerContent', 'answerUrl']);
            return { ...validAnswerOptions };
        });
    }
    return [];
};

export type GenerateBackendResponsesReturn = Session & {
    pTimeMs: number;
    statusId: number;
    responses: Array<Partial<SessionResponse>>;
    inProgressInfo: PureStore['currentQuestion'] | Record<string, never>;
};

export function generateBackendResponses(params: {
    questions: Array<StoreQuestion>;
    location: PureStore['location'];
    session: Session;
    mediaInteractions: PureStore['mediaInteractions'];
    answeringQuestion?: PureStore['currentQuestion'];
    translationId: string;
    newSessionStatus?: number;
    tokens: PureStore['tokens'];
}): GenerateBackendResponsesReturn {
    const { session, questions, location, mediaInteractions, answeringQuestion, translationId, tokens } = params;
    const newSessionStatus = params.newSessionStatus ?? session.statusId;
    const indexOfAnsweringQuestion = questions.findIndex((question) => {
        if (answeringQuestion?.iteration == null) {
            return question.questionId === answeringQuestion?.questionId;
        } else {
            return isSuperset(question, answeringQuestion);
        }
    });
    const validIndexOfAnsweringQuestion = indexOfAnsweringQuestion >= 0 ? indexOfAnsweringQuestion : Number.MAX_SAFE_INTEGER;

    const necessaryQuestions = questions.filter((_, index) => index < validIndexOfAnsweringQuestion);

    const responses: Array<Partial<SessionResponse>> = necessaryQuestions.map((question) => {
        const targetMediaInteractions = mediaInteractions
            .filter((interaction) => interaction.questionId === question.questionId)
            .map((item) => omitObjectKeys(item, ['questionId']));

        const { iteration, questionContent, questionContentTranslation } = question;
        const qContent = questionContentTranslation?.[translationId] ?? questionContent;
        const subResponses = getRespFieldOfQuestion({
            question,
            expiryTimeMs: session.expiryTimeMs,
            allCurrentSurveyQuestions: questions,
            tokens: tokens,
            isPublicSurvey: session.tlTypeId === TRIGGERING_LOGIC_TYPES.PUBLIC,
        });

        const fullAnswerOptions = getFullAnswerOptionsOfChoiceBasedQuestions(question);

        // const commonRespFields = { respTimeMs: Date.now(), iteration, qContent };
        // TODO: Replace with the above line after the backend is updated [The BE update is estimated to take place by the end of October 2024]
        const commonRespFields = iteration == null ? { respTimeMs: Date.now() } : { respTimeMs: Date.now(), iteration, qContent };

        // TODO: Comment `as Array<Resp>` after the backend is updated [The BE update is estimated to take place by the end of October 2024]
        const resp: Array<Resp> = subResponses.map((item) => ({ ...commonRespFields, ...item })) as Array<Resp>;

        return {
            bgColor: '#FEFEFE',
            qId: question.questionId,
            qType: question.questionType,
            userId: session.userId,
            version: session.version,
            loopCount: 0,
            location,
            mediaInteractions: targetMediaInteractions,
            ...resp[0],
            resp,
            ...(fullAnswerOptions.length > 0 && { answerOptions: fullAnswerOptions }),
        };
    });

    return {
        ...session,
        pTimeMs: session.sTimeMs,
        statusId: session.expiryTimeMs < Date.now() ? SESSION_STATUSES.EXPIRED : newSessionStatus,
        responses,
        inProgressInfo: answeringQuestion ?? {},
    };
}
